import { useCallback, useMemo, useSyncExternalStore } from 'react';

import type { ID } from '@nutralab/api';

const STORAGE_KEY = 'catalogView';

interface CatalogView {
    categoryId: ID | null;
    isExpanded: boolean;
}

const DEFAULT_VIEW: CatalogView = { categoryId: null, isExpanded: false };

const listeners = new Set<() => void>();
// Kept in memory as well, so the view survives client navigation even when sessionStorage is unavailable.
let cachedRaw: string | null | undefined;

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

function getSnapshot(): string | null {
    if (cachedRaw === undefined) {
        try {
            cachedRaw = sessionStorage.getItem(STORAGE_KEY);
        } catch {
            cachedRaw = null;
        }
    }
    return cachedRaw;
}

function getServerSnapshot(): null {
    return null;
}

function parseView(raw: string | null): CatalogView {
    if (!raw) {
        return DEFAULT_VIEW;
    }
    try {
        const value = JSON.parse(raw) as Partial<CatalogView>;
        return {
            categoryId: typeof value.categoryId === 'number' ? value.categoryId : null,
            isExpanded: value.isExpanded === true,
        };
    } catch {
        return DEFAULT_VIEW;
    }
}

function writeView(view: CatalogView): void {
    cachedRaw = JSON.stringify(view);
    try {
        sessionStorage.setItem(STORAGE_KEY, cachedRaw);
    } catch {
        // sessionStorage unavailable: the in-memory copy is enough within the tab.
    }
    listeners.forEach((listener) => listener());
}

interface CatalogViewState extends CatalogView {
    setView: (view: CatalogView) => void;
}

/**
 * The home catalog's selected category and "show more" state, kept for the tab session
 * so going back from a product card restores the same list.
 */
export function useCatalogView(): CatalogViewState {
    const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    const view = useMemo(() => parseView(raw), [raw]);
    const setView = useCallback((next: CatalogView) => writeView(next), []);

    return { ...view, setView };
}
